import React, { FC, useMemo } from "react";
import { Box } from "@mui/material";
import { useIntl } from "react-intl";

import { useAlarmPaletteResolver } from "../../../../../../../../../hooks/use-alarm-color";
import { IStationTrainDefectIcon } from "../interfaces";
import { STATION_DEFECT_ICON_SRCS } from "../station-defect-icon-assets";
import {
  buildStationTrainDefectIconRows,
  STATION_TRAIN_DEFECT_ICON_COLUMN_COUNT,
  STATION_TRAIN_DEFECT_ICON_ROW_COUNT,
} from "./station-train-defect-icon-rows";

export interface IStationTrainDefectIconsProps {
  icons: Array<IStationTrainDefectIcon | null>;
}

const CELL_BACKGROUND = "#D9D9D9";

export const StationTrainDefectIcons: FC<IStationTrainDefectIconsProps> = ({ icons }) => {
  const { formatMessage } = useIntl();
  const resolveAlarmColor = useAlarmPaletteResolver();

  const displayIconRows = useMemo(() => buildStationTrainDefectIconRows(icons), [icons]);

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: `repeat(${STATION_TRAIN_DEFECT_ICON_COLUMN_COUNT}, minmax(0, 1fr))`,
        gridTemplateRows: `repeat(${STATION_TRAIN_DEFECT_ICON_ROW_COUNT}, 25px)`,
        gap: "2px",
        width: "100%",
        height: 52,
        flexShrink: 0,
      }}
    >
      {displayIconRows.flat().map((icon, index) => {
        const label = icon
          ? `${formatMessage({ id: `pages.scheme.stationTile.defectIcons.${icon.type}` })}: ${formatMessage({
              id: `enums.alarms.${icon.alarmStatus}`,
              defaultMessage: String(icon.alarmStatus),
            })}`
          : undefined;

        const sx: any = {};
        if (icon) {
          sx.border = "1px solid #8E8E8E";
          sx.borderRadius = "3px";
          sx.backgroundColor = CELL_BACKGROUND;
        }

        return (
          <Box
            key={index}
            aria-label={label}
            aria-hidden={icon ? undefined : true}
            role={icon ? "img" : undefined}
            title={label}
            sx={{
              ...sx,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: 0,
              height: 25,
              visibility: icon ? "visible" : "hidden",
              border: "1px solid #8E8E8E",
              borderRadius: "3px",
              boxSizing: "border-box",
              overflow: "hidden",
            }}
          >
            {icon ? (
              <Box
                component="img"
                src={STATION_DEFECT_ICON_SRCS[icon.type]}
                alt=""
                aria-hidden="true"
                sx={{
                  display: "block",
                  width: "auto",
                  height: "auto",
                  maxWidth: "88%",
                  maxHeight: "88%",
                  objectFit: "contain",
                  backgroundColor: resolveAlarmColor(icon.alarmStatus),
                  pointerEvents: "none",
                }}
              />
            ) : null}
          </Box>
        );
      })}
    </Box>
  );
};
